import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Search, RefreshCw, AlertTriangle, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const QUALITY_COLORS = {
  strong: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  usable: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  weak: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  unavailable: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const PROBE_STATUS_COLORS = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed_fetch: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  auth_error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  no_tenants: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  zero_invoices_fetched: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  skipped: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 4000;

export function XeroInvoiceIdGapPanel() {
  const [dispatching, setDispatching] = useState(false);
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  // Last known-good scan (preserved across failed runs).
  const [lastSuccessResult, setLastSuccessResult] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: load both the latest scan and the latest SUCCESSFUL
  // scan. If the latest is a failure, the last-successful data is
  // preserved so the card is not blanked by a rate-limit error.
  useEffect(() => {
    (async () => {
      try {
        const [latestRes, successRes] = await Promise.all([
          diagnosticsAPI.xeroBackfillJobLatest('scan'),
          diagnosticsAPI.xeroBackfillJobLatestSuccessful('scan'),
        ]);
        const latestJob = latestRes.data?.job;
        const successJob = successRes.data?.job;

        // Always populate last-success if available.
        if (successJob?.status === 'completed' && successJob.result) {
          setLastSuccessResult(successJob.result);
        }

        if (!latestJob) return;
        if (latestJob.status === 'completed' && latestJob.result && !latestJob.result.error) {
          setResult(latestJob.result);
        } else if (latestJob.status === 'running') {
          startTimeRef.current = Date.now();
          startPolling(latestJob._id);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id) => {
    stopPolling();
    setPolling(true);
    startTimeRef.current = startTimeRef.current ?? Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.xeroBackfillJobStatus(id);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          if (data.result?.error) {
            toast.error(data.result.error);
          } else {
            setResult(data.result);
            // If this was a successful scan, update the last-success too.
            const probeStatus = data.result?.xero_probe?.status;
            if (probeStatus === 'completed') {
              setLastSuccessResult(data.result);
              toast.success('Gap scan complete');
            } else if (probeStatus === 'failed_fetch') {
              toast.error('Xero fetch failed (rate-limited or network error). Classification skipped.');
            } else {
              toast.success('Gap scan complete');
            }
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          toast.error(data.error ?? 'Gap scan failed');
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const rebuildIndex = async (tenantId) => {
    if (!window.confirm(
      tenantId
        ? `Rebuild the Xero invoice index for this tenant? This drops existing cached data and performs a full re-fetch.`
        : `Rebuild the Xero invoice index for ALL tenants? This drops all cached data and performs a full re-fetch.`
    )) return;
    setRebuilding(true);
    try {
      const { data } = await diagnosticsAPI.xeroInvoiceIndexRebuild(tenantId);
      if (data.status === 'started') {
        toast.success('Index rebuild started. Run Scan after it completes to see updated results.');
        const rebuildPoll = setInterval(async () => {
          try {
            const { data: jobData } = await diagnosticsAPI.xeroBackfillJobStatus(data.job_id);
            if (jobData.status === 'completed') {
              clearInterval(rebuildPoll);
              setRebuilding(false);
              if (jobData.result?.error) {
                toast.error(jobData.result.error);
              } else {
                toast.success('Index rebuild complete.');
              }
            } else if (jobData.status === 'failed') {
              clearInterval(rebuildPoll);
              setRebuilding(false);
              toast.error(jobData.error ?? 'Rebuild failed');
            }
          } catch {
            clearInterval(rebuildPoll);
            setRebuilding(false);
          }
        }, POLL_INTERVAL_MS);
      } else if (data.status === 'already_running') {
        toast.info('Index rebuild already running.');
        setRebuilding(false);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
      setRebuilding(false);
    }
  };

  const run = async () => {
    setDispatching(true);
    // Don't clear result: keep last-success visible while scanning.
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.xeroGapScanStart();
      startPolling(data.job_id);
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || polling;
  const probe = result?.xero_probe;
  const probeCompleted = probe?.status === 'completed';
  const probeFailed = probe?.status === 'failed_fetch';

  // Show last-successful data when the current result is a failure
  // and a previous successful scan exists.
  const showLastSuccess = probeFailed && lastSuccessResult
    && lastSuccessResult.xero_probe?.status === 'completed';
  const lastSuccessProbe = showLastSuccess ? lastSuccessResult.xero_probe : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Xero Invoice ID Gap Count</CardTitle>
          <div className="flex items-center gap-2">
            {polling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {elapsed}s
              </span>
            )}
            <Button onClick={run} disabled={busy} size="sm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Scan
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Linkage gap count with live Xero match probe. Resolves each unlinked invoice against real Xero data.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Gap count summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-lg font-semibold font-mono">{result.total_invoices}</p>
            </div>
            <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
              <p className="text-xs text-muted-foreground">Linked</p>
              <p className="text-lg font-semibold font-mono text-green-600">{result.linked_count}</p>
            </div>
            <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
              <p className="text-xs text-muted-foreground">Unlinked (gap)</p>
              <p className="text-lg font-semibold font-mono text-amber-600">{result.unlinked_count}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Linkage</p>
              <p className="text-lg font-semibold font-mono text-blue-600">{result.linkage_pct}%</p>
            </div>
          </div>

          {/* Scope note */}
          {result.scope_note && (
            <p className="text-[10px] text-muted-foreground border-l-2 border-muted pl-2">
              {result.scope_note}
            </p>
          )}

          {/* Xero probe results */}
          {probe && (
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Xero Match Probe</p>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${PROBE_STATUS_COLORS[probe.status] ?? ''}`}>
                    {probe.status === 'failed_fetch' ? 'FETCH FAILED' : probe.status}
                  </Badge>
                  {probeCompleted && (
                    <span className="text-[10px] text-muted-foreground">
                      {probe.xero_tenants_searched} tenant{probe.xero_tenants_searched !== 1 ? 's' : ''}, {fmtInt(probe.xero_invoices_fetched)} Xero invoices
                      {probe.per_tenant_counts && probe.per_tenant_counts.length > 1 && (
                        <> ({probe.per_tenant_counts.map(t => `${t.tenantName}: ${fmtInt(t.count)}`).join(', ')})</>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* Quota / rate-limit info */}
              {probe.quota && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground font-mono">
                  <span>API calls: {probe.quota.api_calls_made ?? '-'}</span>
                  <span>Pacing: {probe.quota.pacing_seconds ?? '-'}s/page</span>
                  {probe.quota.day_remaining != null && (
                    <span className={probe.quota.day_remaining < 100 ? 'text-amber-600 font-medium' : ''}>
                      Day remaining: {fmtInt(probe.quota.day_remaining)}
                    </span>
                  )}
                  {probe.quota.minute_remaining != null && (
                    <span>Min remaining: {probe.quota.minute_remaining}</span>
                  )}
                  {probe.quota.estimated_duration_seconds != null && (
                    <span>Duration: ~{probe.quota.estimated_duration_seconds}s</span>
                  )}
                </div>
              )}

              {/* Index info / rebuild */}
              {probe.index && probe.index.tenants && probe.index.tenants.length > 0 && (
                <div className="border rounded-lg p-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium">Persisted Index</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {fmtInt(probe.index.total_in_index)} invoices
                      </span>
                      <Button
                        variant="ghost" size="sm"
                        className="h-6 px-2 text-[10px]"
                        disabled={rebuilding || busy}
                        onClick={() => rebuildIndex(null)}
                      >
                        {rebuilding
                          ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          : <RotateCcw className="h-3 w-3 mr-1" />}
                        Rebuild all
                      </Button>
                    </div>
                  </div>
                  {probe.index.tenants.map((t) => {
                    const syncAge = t.last_synced_at
                      ? `${Math.round((Date.now() - new Date(t.last_synced_at).getTime()) / 60000)}m ago`
                      : 'never';
                    const mode = probe.index.refresh_modes?.[t.tenant_id];
                    return (
                      <div key={t.tenant_id} className="flex items-center justify-between text-[10px] font-mono text-muted-foreground pl-2">
                        <span>
                          {t.tenant_name}: {fmtInt(t.invoice_count)} invoices, synced {syncAge}
                          {mode && <span className="ml-1 text-blue-500">({mode})</span>}
                        </span>
                        <Button
                          variant="ghost" size="sm"
                          className="h-5 px-1.5 text-[10px]"
                          disabled={rebuilding || busy}
                          onClick={() => rebuildIndex(t.tenant_id)}
                        >
                          <RotateCcw className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Failed fetch: prominent error banner, no buckets */}
              {probeFailed && (
                <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">
                      Xero fetch incomplete. Classification skipped.
                    </p>
                  </div>
                  <p className="text-xs text-red-600/80 dark:text-red-400/80">
                    One or more tenants returned errors (likely rate-limited). Classifying against
                    a partial index would produce confident-wrong results. Re-scan after the
                    rate-limit window clears.
                  </p>
                  {probe.failed_tenants && probe.failed_tenants.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-red-600">Failed tenants:</p>
                      {probe.failed_tenants.map((ft, i) => (
                        <p key={i} className="text-[10px] text-red-600/80 font-mono pl-2">
                          {ft.tenantName}: {ft.error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {probeCompleted && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                    <p className="text-[10px] text-muted-foreground">Resolved (unique)</p>
                    <p className="text-lg font-semibold font-mono text-green-600">{probe.resolved_unique}</p>
                    {probe.resolved_unique_pct != null && (
                      <p className="text-[10px] text-muted-foreground">{probe.resolved_unique_pct}%</p>
                    )}
                  </div>
                  <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                    <p className="text-[10px] text-muted-foreground">Ambiguous</p>
                    <p className="text-lg font-semibold font-mono text-amber-600">{probe.resolved_ambiguous}</p>
                    {probe.resolved_ambiguous_pct != null && (
                      <p className="text-[10px] text-muted-foreground">{probe.resolved_ambiguous_pct}%</p>
                    )}
                  </div>
                  <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                    <p className="text-[10px] text-muted-foreground">Not in Xero</p>
                    <p className="text-lg font-semibold font-mono text-red-600">{probe.not_in_xero}</p>
                    {probe.not_in_xero_pct != null && (
                      <p className="text-[10px] text-muted-foreground">{probe.not_in_xero_pct}%</p>
                    )}
                  </div>
                  <div className="border rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">No connected Xero tenant</p>
                    <p className="text-lg font-semibold font-mono text-muted-foreground">{probe.no_tenant}</p>
                    {probe.no_tenant_pct != null && (
                      <p className="text-[10px] text-muted-foreground">{probe.no_tenant_pct}%</p>
                    )}
                  </div>
                </div>
              )}

              {/* Partition line */}
              {probeCompleted && (() => {
                const bucketSum = (probe.resolved_unique ?? 0) + (probe.resolved_ambiguous ?? 0) + (probe.not_in_xero ?? 0) + (probe.no_tenant ?? 0);
                const ok = probe.partition_ok === true;
                return (
                  <p className={`text-[10px] font-mono ${ok ? 'text-muted-foreground' : 'text-red-600 font-medium'}`}>
                    Partition: {probe.resolved_unique ?? 0} + {probe.resolved_ambiguous ?? 0} + {probe.not_in_xero ?? 0} + {probe.no_tenant ?? 0} = {bucketSum} / {result.unlinked_count} unlinked {ok ? '(ok)' : '(MISMATCH)'}
                  </p>
                );
              })()}

              {/* Match-key breakdown within resolved_unique */}
              {probeCompleted && probe.match_key_breakdown && Object.keys(probe.match_key_breakdown).length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Resolved (unique) by match key</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(probe.match_key_breakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([key, count]) => (
                        <span key={key} className="inline-flex items-center gap-1 text-xs border border-green-200 dark:border-green-800 rounded px-2 py-0.5">
                          <span className="font-mono text-green-600">{count}</span>
                          <span className="text-muted-foreground">{key}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Status breakdown within resolved_unique */}
              {probeCompleted && probe.resolved_unique_status_breakdown && Object.keys(probe.resolved_unique_status_breakdown).length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Resolved (unique), BR OS status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(probe.resolved_unique_status_breakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([status, count]) => (
                        <span key={status} className="inline-flex items-center gap-1 text-xs border border-green-200 dark:border-green-800 rounded px-2 py-0.5">
                          <span className="font-mono">{count}</span>
                          <span className="text-muted-foreground">{status}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Status breakdown within not_in_xero */}
              {probeCompleted && probe.not_in_xero_status_breakdown && Object.keys(probe.not_in_xero_status_breakdown).length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Not in Xero, BR OS status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(probe.not_in_xero_status_breakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([status, count]) => (
                        <span key={status} className="inline-flex items-center gap-1 text-xs border border-red-200 dark:border-red-800 rounded px-2 py-0.5">
                          <span className="font-mono">{count}</span>
                          <span className="text-muted-foreground">{status}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {probe.errors && probe.errors.length > 0 && (
                <div className="border rounded-lg p-2 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10">
                  <p className="text-xs font-medium text-red-600 mb-1">Probe errors</p>
                  {probe.errors.map((e, i) => (
                    <p key={i} className="text-[10px] text-red-600/80 font-mono">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Last successful scan (shown when current scan failed) */}
          {showLastSuccess && lastSuccessProbe && (
            <div className="border rounded-lg p-3 space-y-3 border-dashed border-muted-foreground/30">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">Last successful scan</p>
                <Badge className="text-[10px] bg-green-100/50 text-green-700/70 dark:bg-green-900/20 dark:text-green-400/70">
                  completed
                </Badge>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 opacity-75">
                <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                  <p className="text-[10px] text-muted-foreground">Resolved (unique)</p>
                  <p className="text-lg font-semibold font-mono text-green-600">{lastSuccessProbe.resolved_unique}</p>
                </div>
                <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                  <p className="text-[10px] text-muted-foreground">Ambiguous</p>
                  <p className="text-lg font-semibold font-mono text-amber-600">{lastSuccessProbe.resolved_ambiguous}</p>
                </div>
                <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                  <p className="text-[10px] text-muted-foreground">Not in Xero</p>
                  <p className="text-lg font-semibold font-mono text-red-600">{lastSuccessProbe.not_in_xero}</p>
                </div>
                <div className="border rounded-lg p-2 text-center">
                  <p className="text-[10px] text-muted-foreground">No tenant</p>
                  <p className="text-lg font-semibold font-mono text-muted-foreground">{lastSuccessProbe.no_tenant}</p>
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground">
                Generated {lastSuccessResult.generated_at}
              </p>
            </div>
          )}

          {/* Candidate join-key breakdown */}
          {result.candidate_keys && result.candidate_keys.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Candidate join keys</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-1.5">Key</th>
                      <th className="text-right p-1.5">Populated</th>
                      <th className="text-right p-1.5">Missing</th>
                      <th className="text-right p-1.5">Unique (intra-table)</th>
                      <th className="text-right p-1.5">Ambiguous</th>
                      <th className="text-center p-1.5">Intra-table</th>
                      {probeCompleted && (
                        <>
                          <th className="text-right p-1.5">Xero resolved</th>
                          <th className="text-center p-1.5">Xero rate</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {result.candidate_keys.map((ck) => (
                      <tr key={ck.key} className="border-t">
                        <td className="p-1.5 font-mono">{ck.key}</td>
                        <td className="p-1.5 text-right font-mono">{ck.populated}</td>
                        <td className="p-1.5 text-right font-mono text-muted-foreground">{ck.missing}</td>
                        <td className="p-1.5 text-right font-mono text-green-600">{ck.unique_values}</td>
                        <td className="p-1.5 text-right font-mono text-amber-600">{ck.ambiguous_values}</td>
                        <td className="p-1.5 text-center">
                          <Badge className={`text-[10px] ${QUALITY_COLORS[ck.intra_table_quality] ?? ''}`}>
                            {ck.intra_table_quality}
                          </Badge>
                        </td>
                        {probeCompleted && (
                          <>
                            <td className="p-1.5 text-right font-mono">
                              {ck.xero_resolve ? `${ck.xero_resolve.resolved_unique}/${ck.xero_resolve.probed}` : '-'}
                            </td>
                            <td className="p-1.5 text-center font-mono">
                              {ck.xero_resolve ? `${ck.xero_resolve.resolve_rate_pct}%` : '-'}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Intra-table: uniqueness within bros_invoices (secondary).
                {probeCompleted && ' Xero rate: share of probed invoices that resolve to exactly one Xero invoice (primary quality signal).'}
              </p>
            </div>
          )}

          {/* Status breakdown of unlinked */}
          {result.unlinked_status_breakdown && Object.keys(result.unlinked_status_breakdown).length > 0 && (
            <div>
              <p className="text-sm font-medium mb-1">Unlinked invoice status breakdown</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(result.unlinked_status_breakdown)
                  .sort(([, a], [, b]) => b - a)
                  .map(([status, count]) => (
                    <span key={status} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-0.5">
                      <span className="font-mono">{count}</span>
                      <span className="text-muted-foreground">{status}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-right">
            Generated {result.generated_at}
          </p>
        </CardContent>
      )}
    </Card>
  );
}
